import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CatalogItemOwnership } from 'fym-shared/src/smartlinks/catalog';
import { BioLinkAutoAddResult } from 'fym-shared/src/smartlinks/bioLinkSettings';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import {
  useAddReleasedCatalogItemMutation,
  useClaimCatalogItemMutation,
  useGetCatalogQuery,
} from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { ReleasedSongChoice, ReleasedSongPicker } from '../../../../onboarding/addSong/ReleasedSongPicker';
import { AddSongErrorLine } from '../../../../onboarding/addSong/AddSongErrorLine';
import { findExistingSong } from '../../../../onboarding/addSong/findExistingSong';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { addSongErrorOf, AddSongErrorKind } from '../addSongError';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { FirstSongKind } from '../useOnboardingFirstSong';
import { BioLinkTick } from '../useBioLinkTick';
import { BioLinkTickRow } from '../BioLinkTickRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongLinkPathProps {
  accountId: number;
  artistName: string;
  eyebrow: string;
  track: TrackSave;
  onSaved: (item: CatalogItem, kind: FirstSongKind | null, bioLink?: BioLinkAutoAddResult) => void;
  onTypeInstead: () => void;
  /** The first input of the path (the onboarding analytics). */
  onStarted?: (kind: 'claim' | 'link') => void;
  /** "Auto add to my Bio Link" on the confirm step (design smartlink-editor e1 note). */
  bioTick: BioLinkTick;
  /** `find`: the artist wants to search for the song; `paste`: the artist has the link. One field takes both. */
  mode: SongLinkPathMode;
  /** The title typed on step 1: the search starts with it. */
  initialQuery?: string;
}

export type SongLinkPathMode = 'find' | 'paste';

/**
 * Step 1 for a song that is out (design catalog-add-song c1-c3): search or paste a link in one field.
 * "Find it" asks for a search, "Paste a link" for a link; only the question above the field differs. A search
 * pick is claimed (`POST .../catalog/claim`); a link is added with what it gave: the title, the
 * artist, the cover, the service links and the ISRC (`POST .../catalog/released`).
 */
const SongLinkPathComponent: React.FC<SongLinkPathProps> = ({
  accountId,
  artistName,
  eyebrow,
  track,
  onSaved,
  onTypeInstead,
  onStarted,
  bioTick,
  mode,
  initialQuery,
}) => {
  const intl = useIntl();
  const [claim, claiming] = useClaimCatalogItemMutation();
  const [addReleased, adding] = useAddReleasedCatalogItemMutation();
  const [error, setError] = useState<AddSongErrorKind>();
  const [lastChoice, setLastChoice] = useState<ReleasedSongChoice>();
  const isExists = error === 'exists';
  const catalog = useGetCatalogQuery({ accountId }, { skip: !isExists });
  const isSaving = claiming.isLoading || adding.isLoading;

  const facts = useMemo(() => {
    if (lastChoice === undefined) {
      return undefined;
    }
    return lastChoice.kind === 'claim'
      ? {
          title: lastChoice.snapshot.title,
          catalogTrackMaid: lastChoice.snapshot.catalogTrackMaid,
          serviceLinks: lastChoice.snapshot.serviceLinks,
        }
      : {
          title: lastChoice.input.title,
          catalogTrackMaid: lastChoice.input.catalogTrackMaid,
          serviceLinks: lastChoice.input.serviceLinks,
        };
  }, [lastChoice]);
  const existing = useMemo(
    () => (isExists && facts !== undefined ? findExistingSong(catalog.data?.items ?? [], facts) : undefined),
    [catalog.data, facts, isExists]
  );

  const onSave = useCallback(
    (choice: ReleasedSongChoice) => {
      setError(undefined);
      setLastChoice(choice);
      const { addToBioLink } = bioTick;
      const saving =
        choice.kind === 'claim'
          ? claim({ accountId, ownership: CatalogItemOwnership.mine, ...choice.snapshot, addToBioLink }).unwrap()
          : addReleased({ accountId, ...choice.input, addToBioLink }).unwrap();
      track(saving)
        .then((result) => onSaved(result.item, choice.kind, result.bioLink))
        .catch((failure: unknown) => setError(addSongErrorOf(failure)));
    },
    [accountId, addReleased, bioTick, claim, onSaved, track]
  );

  const started = useCallback((kind: 'claim' | 'link') => onStarted?.(kind), [onStarted]);
  const openExisting = useCallback(() => {
    if (existing !== undefined) {
      onSaved(existing, null);
    }
  }, [existing, onSaved]);

  return (
    <>
      <StepHeader
        eyebrow={eyebrow}
        question={intl.formatMessage(
          mode === 'find' ? addSongFlowMessages.findQuestion : addSongFlowMessages.linkQuestion
        )}
        sub={intl.formatMessage(mode === 'find' ? addSongFlowMessages.findSub : addSongFlowMessages.linkSub)}
      />
      {error === 'exists' && (
        <AddSongErrorLine
          reason="exists"
          title={facts?.title ?? ''}
          onOpenExisting={existing === undefined ? undefined : openExisting}
        />
      )}
      {error === 'save' && <AddSongErrorLine reason="save" title="" />}
      {error === 'readOnly' && (
        <div className={styles.alertError} role="alert">
          {intl.formatMessage(addSongFlowMessages.readOnlyArtist)}
        </div>
      )}
      <ReleasedSongPicker
        artistName={artistName}
        isSaving={isSaving}
        onStarted={started}
        onSave={onSave}
        onAddUnreleased={onTypeInstead}
        confirmSlot={<BioLinkTickRow tick={bioTick} disabled={isSaving} />}
        initialQuery={initialQuery}
      />
      <div>
        <SMLButton color="link" omitCap onClick={onTypeInstead} testID="add-song-type-instead">
          {intl.formatMessage(addSongFlowMessages.linkTypeInstead)}
        </SMLButton>
      </div>
    </>
  );
};

export const SongLinkPath = TypedMemo(SongLinkPathComponent);
